// Classes of micro-2d-app's shadow-DOM UI overlays that must be excluded from
// dealer comparison snapshots. We want a clean product image of the cars only —
// no nav arrows, fullscreen button, page counter, or compare-mode toggle bar.
const SNAPSHOT_EXCLUDED_CLASSES = new Set([
  'carousel__buttons', // left/right navigation arrows
  'carousel__selected-snap-wrapper', // "1/3" page counter
  'carousel__resize-button-wrapper', // fullscreen toggle
  'compare-button-wrapper', // swap / slider / compare-mode buttons row
]);

export interface ICaptureElementSnapshotOptions {
  /**
   * Output MIME type. Defaults to `image/png` — the dealer comparison export, which is what this
   * function was written for, needs the alpha channel from the source car renders.
   *
   * A caller that asks for a lossy type gets whatever the browser's encoder actually produces:
   * WebKit silently falls back to PNG for `image/webp` rather than failing, so read `blob.type`
   * before labelling the result.
   */
  type?: string;
  /** Encoder quality, 0-1. Ignored for lossless types. */
  quality?: number;
  /**
   * Pre-fetch every `<img>` in the subtree and inline it as a data URL before serialising.
   * Defaults to `true` — see the AVS-4167 note below, which is why the dealer export needs it.
   *
   * AVS-4434 passes `false`. The Session Flight Recorder captures this element while a session is
   * already failing, and the inlining path issues one forced revalidation (`cache: 'reload'`) per
   * image on the very uplink under investigation. A diagnostic must not add load to the fault it
   * is diagnosing; a snapshot with unrendered images still carries the layout, the toast and the
   * error banner, which is the whole reason it is taken.
   */
  inlineImages?: boolean;
  /**
   * A CSS selector for nodes to redact from the snapshot. Every node matched by the selector, and
   * everything under it, is dropped from html-to-image's rendered clone — html-to-image `^1.11.13`
   * has no `onClone` hook to blank a node's text in place, so the masked node's box simply collapses
   * out of the image instead. That is acceptable for a debugging snapshot.
   *
   * The live DOM is never touched: mutating it to mask a node — even for the ~100ms a capture takes
   * — would flash the redaction on the dealer's own screen mid-call. AVS-4423 adds this for the
   * flight recorder's DOM snapshot, which is why `captureDomSnapshot` is the only caller that sets
   * it; the dealer comparison export (this function's original caller) leaves it unset.
   */
  maskSelector?: string;
  /**
   * Passed straight through to html-to-image's own `skipFonts` option — skips its font
   * download-and-embed pass. Plumbed here (rather than hardcoded) so a caller that needs the fonts
   * embedded (the dealer comparison export keeps its default of `false`) is unaffected.
   */
  skipFonts?: boolean;
}

/**
 * Captures an HTML element as an image blob using html-to-image.
 * Defaults to PNG so the snapshot preserves the alpha channel from the
 * source car renders (transparent backgrounds), letting dealers use the image
 * as a clean product cutout. Uses dynamic import for code splitting.
 */
export async function captureElementSnapshot(
  el: HTMLElement,
  options: ICaptureElementSnapshotOptions = {}
): Promise<Blob> {
  if (!el) {
    throw new Error('Element is required for snapshot capture');
  }

  const { type = 'image/png', quality, inlineImages = true, maskSelector, skipFonts } = options;

  // Pre-flight guard: html-to-image sizes the canvas from clientWidth/clientHeight.
  // A 0x0 canvas causes `canvas.toBlob(cb)` to call back with null — an opaque
  // failure. Fail fast with element identity for diagnosis.
  if (el.clientWidth === 0 || el.clientHeight === 0) {
    throw new Error(`Cannot capture element with zero dimensions: ${describeElement(el)}`);
  }

  // AVS-4167: Inline every <img> inside the capture target as a data URL
  // BEFORE handing off to html-to-image. Two prior approaches failed:
  //   1. fetchRequestInit:{cache:'reload'} — controls HTTP cache only, not
  //      WebKit's per-resource image memory cache.
  //   2. cacheBust:true — the CDN serving the car renders (mediaservice.audi.com)
  //      uses a signed URL and rejects any extra query string with HTTP 400,
  //      so a cache-busted URL never resolves at all.
  // The actual pipeline is implemented in `loadAsCorsDataUrl`: fetch() the bytes
  // in CORS mode (bypasses WebKit's per-resource image cache), then decode the
  // resulting blob via a same-origin blob: URL into a fresh Image, draw onto a
  // canvas, and re-encode with `canvas.toDataURL('image/png')`. Re-encoding to
  // PNG preserves the source alpha channel (JPEG would collapse to opaque black)
  // and normalises WebP into a format WebKit's foreignObject renderer accepts.
  // html-to-image never has to fetch — it sees a data URL and embeds directly.
  const restoreImages = inlineImages ? await inlineImagesAsDataUrls(el) : noRestore;

  try {
    const { toBlob } = await import('html-to-image');

    // WebKit workaround: call toBlob TWICE. html-to-image renders the cloned
    // DOM into an <svg><foreignObject> container, serialises that SVG to a
    // data URL, loads it into an Image, then drawImage()s onto a canvas.
    // Safari's outer-Image onload fires before inner data-URL <img>s finish
    // rasterising inside the foreignObject, so the first canvas snapshot is
    // blank. The second call has warm decoded state and produces the real
    // image. Chromium completes on the first call but tolerates the second.
    const filter = buildSnapshotFilter(maskSelector);

    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      blob = await toBlob(el, {
        type,
        quality,
        // Skip shadow-DOM UI overlays — dealers want a clean product image — composed with the
        // caller's redaction selector, if any.
        filter,
        skipFonts,
        // Defense-in-depth for any remote resource we couldn't inline
        // (CSS background-image, SVG xlink:href).
        fetchRequestInit: { cache: 'reload' },
      });
    }

    if (!blob) {
      throw new Error('Failed to capture element snapshot');
    }

    return blob;
  } finally {
    restoreImages();
  }
}

/** The restore step for `inlineImages: false` — nothing was swapped, so nothing is put back. */
function noRestore(): void {}

function snapshotNodeFilter(node: HTMLElement): boolean {
  if (!node.classList) return true;
  for (const cls of SNAPSHOT_EXCLUDED_CLASSES) {
    if (node.classList.contains(cls)) return false;
  }
  return true;
}

/**
 * Composes {@link snapshotNodeFilter} with a mask predicate for `maskSelector`, when one is given.
 * `node.closest?.(maskSelector)` — not just `matches` — is what drops a masked node's DESCENDANTS
 * too: html-to-image walks the tree top-down and stops recursing into a node its filter rejects, but
 * being defensive here costs nothing and keeps the guarantee explicit rather than implicit in
 * traversal order.
 */
function buildSnapshotFilter(maskSelector: string | undefined): (node: HTMLElement) => boolean {
  if (!maskSelector) return snapshotNodeFilter;

  return (node: HTMLElement): boolean => {
    if (node.matches?.(maskSelector) || node.closest?.(maskSelector)) return false;
    return snapshotNodeFilter(node);
  };
}

function describeElement(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = el.className ? `.${el.className.trim().split(/\s+/).join('.')}` : '';
  return `${tag}${id}${classes}`;
}

function collectImages(root: HTMLElement): HTMLImageElement[] {
  const found: HTMLImageElement[] = [];
  const visit = (node: ParentNode) => {
    node.querySelectorAll('img').forEach((img) => found.push(img));
    node.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) visit(el.shadowRoot);
    });
  };
  visit(root);
  if (root.shadowRoot) visit(root.shadowRoot);
  return found;
}

/**
 * Pre-converts every <img> reachable from `root` (including shadow DOM) into a
 * data URL stored on its own src. Returns a restore function that puts the
 * original src values back. Individual conversion failures are swallowed so a
 * single broken image doesn't abort the whole snapshot — that image will simply
 * render blank in the output (same as html-to-image's own behaviour).
 */
async function inlineImagesAsDataUrls(root: HTMLElement): Promise<() => void> {
  const images = collectImages(root);
  const originals: Array<[HTMLImageElement, string]> = [];

  await Promise.all(
    images.map(async (img) => {
      const originalSrc = img.src;
      if (!originalSrc || originalSrc.startsWith('data:')) return;

      try {
        const dataUrl = await loadAsCorsDataUrl(originalSrc);
        originals.push([img, originalSrc]);
        img.src = dataUrl;
      } catch {
        // Conversion failed (network error, tainted canvas, etc.). Leave the
        // original src in place — html-to-image will attempt its own fetch
        // path and at worst this single image renders blank in the snapshot.
      }
    })
  );

  return () => {
    originals.forEach(([img, src]) => {
      img.src = src;
    });
  };
}

async function loadAsCorsDataUrl(url: string): Promise<string> {
  // Two stages — both required for WebKit:
  //
  // Stage A: fetch() the bytes ourselves with mode:'cors' + cache:'reload'.
  // Bypasses the HTMLImageElement code path entirely. `<img>` in WebKit shares
  // a per-resource memory cache that retains the original no-CORS page-render
  // load even when crossOrigin='anonymous' is set on a probe Image. fetch()
  // uses a separate response cache and reliably issues a fresh CORS-mode
  // request, returning the raw bytes.
  //
  // Stage B: decode the blob and re-encode as PNG via a same-origin blob URL.
  // We can't just FileReader-encode the blob bytes into a data:image/webp;...
  // URL: WebKit's SVG-foreignObject renderer (used internally by html-to-image)
  // does NOT decode WebP inside <img> children of foreignObject, so the final
  // snapshot comes back blank even though the data URL is well-formed.
  // Decoding via canvas and re-encoding with canvas.toDataURL('image/png')
  // gives us a plain PNG data URL that WebKit's renderer handles correctly,
  // and the canvas is untainted (blob: URLs are same-origin).
  const blob = await fetchAsBlob(url);
  return await reencodeBlobAsPngDataUrl(blob);
}

async function fetchAsBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors', cache: 'reload' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

function reencodeBlobAsPngDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // Scoped inside the executor so the matching revoke is paired only when
    // createObjectURL actually succeeded — avoids revokeObjectURL(undefined)
    // if URL is unavailable in some exotic host context.
    const objectUrl = URL.createObjectURL(blob);
    const cleanup = () => URL.revokeObjectURL(objectUrl);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          reject(new Error('2D canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        cleanup();
        resolve(dataUrl);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };
    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to decode image blob'));
    };
    img.src = objectUrl;
  });
}
